import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { payloadString } from '../realtime-sync.payload';
import type { DataChangedEvent } from '../realtime-sync.types';
import { useJobQueueStore } from '@/features/design/job-queue';

export async function handleControlCompleted(event: DataChangedEvent): Promise<void> {
  const store = useJobQueueStore.getState();
  const payload = event.payload;

  wsLogger.info('[RealtimeSync] Control completed:', {
    controlId: event.id,
    fileUrl: payload?.file_url,
  });

  // Update control status in store
  if (event.id) {
    store.updateControlStatus(event.id, 'completed');
  }

  // Invalidate the per-angle controls list (payload carries angle_id as of the
  // unified realtime contract). Fall back to the broad invalidation if the
  // payload is missing angle_id for any reason (older clients, unusual flows).
  const angleId = payloadString(payload, 'angle_id');
  if (angleId) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.controls.listByAngle(angleId),
    });
  } else {
    queryClient.invalidateQueries({ queryKey: cacheKeys.controls.all });
  }
}

export async function handleControlFailed(event: DataChangedEvent): Promise<void> {
  const store = useJobQueueStore.getState();
  const payload = event.payload;

  wsLogger.warn('[RealtimeSync] Control failed:', {
    controlId: event.id,
    error: payload?.error,
  });

  if (event.id) {
    store.updateControlStatus(event.id, 'failed');
  }

  // Show error toast — API-driven only (skip if payload lacks localized title).
  const controlFailedTitle = payloadString(payload, 'title');
  if (controlFailedTitle) {
    const description =
      payloadString(payload, 'message') ?? payloadString(payload, 'error');
    toast.error(controlFailedTitle, {
      ...(description && { description }),
      duration: 6000,
    });
  }
}
